import {createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {isAxiosError} from 'axios'
import i18n from '@/i18n'
import {api, registrarVencimientoDeSesion, TOKEN_KEY} from '@/lib/api'
import type {components} from '@/lib/api-schema'
import {publicarAviso} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {clearTelemetry, identifyTelemetry} from '@/lib/telemetry'

type User = components['schemas']['UserRead']

type AuthContextValue = {
  user: User | null
  loading: boolean
  signIn: (cedula: string, password: string) => Promise<{error: string | null}>
  completeAuth: (accessToken: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

async function loadUser(): Promise<User> {
  const user = (await api.get<User>('/auth/me')).data
  identifyTelemetry(user.id)
  return user
}

export function AuthProvider({children}: {children: ReactNode}) {
  const queryClient = useQueryClient()
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  // Fuera del estado a propósito: lo lee el manejador de 401, que corre dentro de un
  // interceptor de axios y vería congelado el `user` del render en que se registró.
  const sesionActiva = useRef(false)

  const signOut = useCallback(() => {
    sesionActiva.current = false
    clearTelemetry()
    queryClient.clear()
    localStorage.removeItem(TOKEN_KEY)
    setUser(null)
  }, [queryClient])

  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) {
      setLoading(false)
      return
    }
    loadUser()
      .then((cargado) => {
        sesionActiva.current = true
        setUser(cargado)
      })
      // Solo un 401 invalida la credencial: borrar el token por un fallo de red cerraría la
      // sesión en todas las pestañas, que comparten `localStorage`.
      .catch((err) => {
        if (isAxiosError(err) && err.response?.status === 401) localStorage.removeItem(TOKEN_KEY)
      })
      .finally(() => setLoading(false))
  }, [])

  // El pestillo evita que la ráfaga de 401 de una pantalla entera cierre la sesión una vez
  // por consulta.
  useEffect(() => {
    registrarVencimientoDeSesion(() => {
      if (!sesionActiva.current) return
      signOut()
      publicarAviso(i18n.t('avisos.sesionVencida.titulo'), i18n.t('avisos.sesionVencida.detalle'))
    })
    return () => registrarVencimientoDeSesion(null)
  }, [signOut])

  async function signIn(cedula: string, password: string) {
    try {
      const {data} = await api.post('/auth/login', {cedula, password})
      await completeAuth(data.access_token)
      return {error: null}
    } catch (err) {
      return {error: translateApiError(err)}
    }
  }

  // Se limpia primero: la caché se indexa por empresa y empleado, nunca por quién pregunta,
  // así que otra identidad en la misma pestaña vería lecturas ajenas.
  async function completeAuth(accessToken: string) {
    queryClient.clear()
    localStorage.setItem(TOKEN_KEY, accessToken)
    const cargado = await loadUser()
    sesionActiva.current = true
    setUser(cargado)
  }

  return <AuthContext.Provider value={{user, loading, signIn, completeAuth, signOut}}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
